import { useCallback } from "react"

import { 
  GET_TIMETABLE, 
  GET_TIMETABLE_BOOKED_SLOT_DETAILS,
  GET_TIMETABLE_CHANGES, 
} from "@graphql"

import { NetworkStatus, useLazyQuery, useQuery } from "@apollo/client"

const useTimetable = (sessionId) => {
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_TIMETABLE, {
    variables: { input: { id: sessionId } },
    skip: !sessionId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  })

  const isInitialLoad = loading && networkStatus === NetworkStatus.loading;
  const isRefetching = networkStatus === NetworkStatus.refetch;  

  const [getTimetableChanges, { loading: partialLoading, error: partialError }] = useLazyQuery(GET_TIMETABLE_CHANGES, {
    fetchPolicy: "network-only",
  })

  const [getBookedSlotDetails, { data: bookedSlotDetailsData, loading: bookedSlotDetailsLoading, error: bookedSlotDetailsError }] = useLazyQuery(GET_TIMETABLE_BOOKED_SLOT_DETAILS, {
    fetchPolicy: "network-only",
  })

  const fetchBookedSlotDetails = useCallback((eventId, bookingSlotId) => {
    return getBookedSlotDetails({
      variables: {
        input: {
          event_id: eventId,
          booking_slot_id: bookingSlotId,
        },
      },
    }).then((response) => response?.data?.sessionTimetableBookedSlotDetails)
  }, [getBookedSlotDetails])

  const fullRefetch = useCallback(() => {
    return refetch({ input: { id: sessionId } }).then((response) => response?.data?.sessionTimetable)
  }, [refetch, sessionId])

  const partialRefetch = (lastUpdatedAt) => {
    return getTimetableChanges({ 
      variables: {
        input: { 
          id: sessionId, 
          last_updated_at: lastUpdatedAt 
        } 
      }
    }).then((response) => response.data.sessionTimetableChanges)
  }

  const updateTimetableDeletedBookingCache = (cache, bookingId, slotId) => {
    cache.modify({
      id: cache.identify({ __typename: "SessionTimetableSlot", id: slotId }),
      fields: {
        status: () => "AVAILABLE",
        booking: () => null,
      },
    })

    cache.evict({
      id: cache.identify({ __typename: "SessionTimetableBooking", id: bookingId }),
    })

    cache.gc()
  }

  /**
   * Skipped slots are those that were attempted to be updated but couldn't be because they have been booked in the meantime.
   */
  const updateTimetableAvailabilityCache = (cache, updatedSlots, skippedSlots) => {
    skippedSlots.forEach(skippedSlot => {
      cache.modify({
        id: cache.identify({ __typename: "SessionTimetableSlot", id: skippedSlot.id }),
        fields: { 
          status: () => "BOOKED",
          booking: () => {
            const [bookingSlot] = skippedSlot.bookingSlots ?? null
            
            if (!bookingSlot) return null;

            return {
              __typename: "SessionTimetableBooking",
              id: bookingSlot.booking_id,
              student_initials: bookingSlot.student.forename.charAt(0) + bookingSlot.student.surname.charAt(0),
              booking_slot_id: bookingSlot.id,
              student_id: bookingSlot.student.id,
            }
          }
        },
      })
    })
      
    updatedSlots.forEach(updatedSlot => {
      cache.modify({
        id: cache.identify({ __typename: "SessionTimetableSlot", id: updatedSlot.id }),
        fields: { status: () => updatedSlot.status.toUpperCase() },
      })
    })
  }

  return {
    bookedSlotDetails: bookedSlotDetailsData?.getBookedSlotDetails ?? null,
    bookedSlotDetailsLoading,
    bookedSlotDetailsError,
    fetchBookedSlotDetails,
    timetable: data ? data.sessionTimetable : null,
    loading: isInitialLoad,
    isRefetching,
    error,
    fullRefetch,
    partialError,
    partialLoading,
    partialRefetch,
    updateTimetableAvailabilityCache,
    updateTimetableDeletedBookingCache,
  }
}

export default useTimetable